<template>
    <UForm ref="formRef" :state="form" :schema="schema" class="flex flex-col" @submit="save">
        <div class="flex flex-col gap-4 p-4 sm:p-6">
            <UFormField :label="$t('task.title')" name="title" required>
                <UInput v-model="form.title" :maxlength="TaskLimits.Title" class="w-full" />
            </UFormField>

            <UFormField :label="$t('task.description')" name="description">
                <UTextarea
                    v-model="form.description"
                    :rows="3"
                    autoresize
                    :maxrows="14"
                    :maxlength="TaskLimits.Description"
                    :placeholder="$t('task.description_placeholder')"
                    class="w-full"
                />
            </UFormField>

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <UFormField :label="$t('task.status')" name="status" required>
                    <USelect v-model="form.status" :items="statusItems" value-key="value" class="w-full" />
                </UFormField>

                <UFormField :label="$t('task.priority')" name="priority" required>
                    <USelect v-model="form.priority" :items="priorityItems" value-key="value" class="w-full" />
                </UFormField>

                <UFormField :label="$t('task.assignee')" name="assignee_id" :help="$t('task.assignee_help')" class="sm:col-span-2">
                    <USelect
                        v-model="assigneeModel"
                        :items="assigneeItems"
                        value-key="value"
                        icon="i-lucide-user-round"
                        class="w-full"
                    />
                </UFormField>

                <UFormField :label="$t('task.start_date')" name="start_date">
                    <DateInput v-model="form.start_date" :disabled="form.milestone" />
                </UFormField>

                <UFormField :label="form.milestone ? $t('task.milestone_date') : $t('task.due_date')" name="due_date">
                    <DateInput v-model="form.due_date" />
                </UFormField>

                <UFormField name="milestone" class="sm:col-span-2">
                    <USwitch v-model="form.milestone" :label="$t('task.milestone')" :description="$t('task.milestone_help')" />
                </UFormField>

                <UFormField :label="$t('task.estimate')" name="estimate_hours" :help="$t('task.estimate_help')">
                    <UInputNumber
                        v-model="estimateModel"
                        :min="0"
                        :max="TaskLimits.Estimate"
                        :step="0.5"
                        :format-options="{ maximumFractionDigits: 2 }"
                        :placeholder="$t('task.estimate_none')"
                        class="w-full"
                    />
                </UFormField>

                <UFormField :label="$t('task.progress_value', { value: form.progress })" name="progress">
                    <div class="flex h-8 items-center">
                        <USlider
                            v-model="form.progress"
                            :min="0"
                            :max="100"
                            :step="5"
                            :disabled="form.status === TaskStatus.Done"
                            class="w-full"
                        />
                    </div>
                </UFormField>
            </div>

            <!-- Finish-to-start inside one project (docs/API.md → Scheduling rules) — for whoever
                 can see the project. -->
            <USeparator v-if="task.project.can_view" />

            <UFormField
                v-if="task.project.can_view"
                :label="$t('task.predecessors')"
                name="depends_on"
                :help="$t('task.predecessors_help')"
            >
                <USelectMenu
                    v-model="form.depends_on"
                    :items="predecessorItems"
                    value-key="value"
                    multiple
                    icon="i-lucide-link"
                    :placeholder="$t('task.predecessors_none')"
                    :search-input="{ placeholder: $t('general.search') }"
                    class="w-full"
                />
            </UFormField>

            <div v-if="task.project.can_view && task.predecessors.length" class="-mt-2 flex flex-wrap gap-1.5">
                <UButton
                    v-for="item in task.predecessors"
                    :key="item.id"
                    :label="`${item.key} ${item.title}`"
                    :icon="item.status === TaskStatus.Done ? 'i-lucide-circle-check' : 'i-lucide-circle-dashed'"
                    color="neutral"
                    variant="subtle"
                    size="xs"
                    class="max-w-full"
                    :ui="{ label: 'truncate' }"
                    @click="taskDrawer.open(item.id)"
                />
            </div>

            <div v-if="task.project.can_view">
                <p class="mb-1.5 text-sm font-medium text-default">{{ $t('task.successors') }}</p>
                <p v-if="task.successors.length === 0" class="text-sm text-muted">{{ $t('task.successors_none') }}</p>
                <div v-else class="flex flex-wrap gap-1.5">
                    <UButton
                        v-for="item in task.successors"
                        :key="item.id"
                        :label="`${item.key} ${item.title}`"
                        icon="i-lucide-arrow-right"
                        color="neutral"
                        variant="subtle"
                        size="xs"
                        class="max-w-full"
                        :ui="{ label: 'truncate' }"
                        @click="taskDrawer.open(item.id)"
                    />
                </div>
            </div>

            <dl class="grid grid-cols-1 gap-x-4 gap-y-1 text-xs text-dimmed sm:grid-cols-2">
                <div>
                    <dt class="inline">{{ $t('task.created_meta') }}:</dt>
                    <dd class="inline"> {{ task.created_by?.name }}, {{ formatDateTime(task.created_at) }}</dd>
                </div>
                <div v-if="task.completed_at">
                    <dt class="inline">{{ $t('task.completed') }}:</dt>
                    <dd class="inline"> {{ formatDateTime(task.completed_at) }}</dd>
                </div>
            </dl>
        </div>

        <!-- The actions stay in view however long the form is. -->
        <div class="sticky bottom-0 flex items-center justify-between gap-2 border-t border-default bg-default px-4 py-3 sm:px-6">
            <UButton
                v-if="canDelete"
                :label="$t('general.delete')"
                icon="i-lucide-trash-2"
                color="error"
                variant="ghost"
                @click="remove"
            />
            <span v-else />

            <UButton :label="$t('general.save')" type="submit" icon="i-lucide-check" :loading="appStore.isLoaderActive()" />
        </div>
    </UForm>

    <AppConfirmDialog ref="confirmDialog" />
</template>

<script lang="ts" setup>
    import { computed, ref, useTemplateRef, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import * as z from 'zod'

    import AppConfirmDialog from '@/components/app/AppConfirmDialog.vue'
    import DateInput from '@/components/DateInput.vue'
    import { TaskLimits, TaskStatus } from '@/enums/task'
    import { formatDateTime, formatDayShort } from '@/libs/dayjs'
    import { Errors, errorCode, parseKnownError } from '@/libs/error'
    import { EMPTY_OPTION } from '@/libs/select'
    import { formatShifted } from '@/libs/schedule'
    import { taskPriorityOptions, taskStatusOptions, taskUpdateRequest } from '@/libs/task'
    import { PrintError, PrintNotice, PrintSuccess } from '@/libs/toast'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import { useTaskDrawerStore } from '@/stores/taskDrawer'
    import type { SelectOption } from '@/types/Common'
    import type { IProjectMember } from '@/types/Project'
    import type { IScheduleResult, ITask, ITaskDetail, ITaskMin, ITaskUpdateRequest } from '@/types/Task'

    /*
     * EVERYTHING ABOUT A TASK THAT CAN BE TYPED, in one form with one Save.
     *
     * Two endpoints stand behind it (docs/API.md → Tasks): PUT /tasks/{id} for the fields and
     * PUT /tasks/{id}/dependencies for the predecessors. Save sends only what changed, fields
     * first; both answers can carry `shifted` — the successors the cascade moved later — and
     * the reader is told about all of them in one notice. A predecessor that would close a
     * loop (15) or belongs to another project (16) is said under the field itself.
     */
    interface DetailsForm {
        title:          string
        description:    string
        status:         string
        priority:       string
        assignee_id:    string
        start_date:     string | null
        due_date:       string | null
        milestone:      boolean
        estimate_hours: number | null
        progress:       number
        depends_on:     string[]
    }

    const props = defineProps<{
        task:         ITaskDetail
        members:      IProjectMember[]
        projectTasks: ITask[]
        canDelete:    boolean
    }>()

    const emit = defineEmits<{
        (e: 'saved', task: ITask, shifted: ITaskMin[]): void
        (e: 'deleted', id: string): void
    }>()

    const { t }         = useI18n()
    const appStore      = useAppStore()
    const fetchStore    = useFetchStore()
    const taskDrawer    = useTaskDrawerStore()
    const formRef       = useTemplateRef<{ setErrors: (errors: { name: string, message: string }[]) => void }>('formRef')
    const confirmDialog = useTemplateRef<InstanceType<typeof AppConfirmDialog>>('confirmDialog')

    const fromTask = (task: ITaskDetail): DetailsForm => ({
        title:          task.title,
        description:    task.description ?? '',
        status:         task.status,
        priority:       task.priority,
        assignee_id:    task.assignee?.id ?? '',
        start_date:     task.start_date,
        due_date:       task.due_date,
        milestone:      task.milestone,
        estimate_hours: task.estimate_hours,
        progress:       task.progress,
        depends_on:     [...(task.depends_on ?? [])],
    })

    const form = ref<DetailsForm>(fromTask(props.task))

    // A milestone is one day: the server keeps its start on its due date, and so does the form.
    watch(() => [form.value.milestone, form.value.due_date] as const, ([milestone, due]) => {
        if (milestone) form.value.start_date = due
    })

    // Done is 100 %, whatever the slider said (the server forces it as well).
    watch(() => form.value.status, (status) => {
        if (status === TaskStatus.Done) form.value.progress = 100
    })

    const statusItems   = computed((): SelectOption[] => taskStatusOptions())
    const priorityItems = computed((): SelectOption[] => taskPriorityOptions())

    /*
     * The people a task can be given to: the project's members — lead included — and nobody
     * else (code 14). An assignee who has since left the project is still listed, marked, so
     * opening the task does not quietly unassign them.
     */
    const assigneeItems = computed((): SelectOption[] => {
        const items: SelectOption[] = [{ label: t('task.unassigned'), value: EMPTY_OPTION }]

        for (const member of props.members) items.push({ label: member.user.name, value: member.user.id })

        const current = props.task.assignee

        if (current && !props.members.some((member) => member.user.id === current.id)) {
            items.push({ label: t('task.assignee_not_member_label', { name: current.name }), value: current.id })
        }

        return items
    })

    const assigneeModel = computed({
        get: (): string => form.value.assignee_id || EMPTY_OPTION,
        set: (value: string) => { form.value.assignee_id = value === EMPTY_OPTION ? '' : value },
    })

    // The number input reports a cleared field as undefined or NaN; the API wants null.
    const estimateModel = computed({
        get: (): number | undefined => form.value.estimate_hours ?? undefined,
        set: (value: number | null | undefined) => {
            form.value.estimate_hours = typeof value === 'number' && !Number.isNaN(value) ? value : null
        },
    })

    /** Every other task of the project, with its dates, to pick predecessors from. */
    const predecessorItems = computed((): (SelectOption & { description?: string })[] => props.projectTasks
        .filter((item) => item.id !== props.task.id)
        .map((item) => ({
            label:       `${item.key} ${item.title}`,
            value:       item.id,
            description: [formatDayShort(item.start_date), formatDayShort(item.due_date)].filter(Boolean).join(' – ') || undefined,
        })))

    const schema = computed(() => z.object({
        title:          z.string().trim().min(1, t('validation.required')).max(TaskLimits.Title, t('validation.too_long')),
        description:    z.string().max(TaskLimits.Description, t('validation.too_long')),
        status:         z.string().min(1, t('validation.required')),
        priority:       z.string().min(1, t('validation.required')),
        assignee_id:    z.string(),
        start_date:     z.string().nullable(),
        due_date:       z.string().nullable(),
        milestone:      z.boolean(),
        estimate_hours: z.number().min(0, t('task.estimate_range')).max(TaskLimits.Estimate, t('task.estimate_range')).nullable(),
        progress:       z.number().min(0).max(100),
        depends_on:     z.array(z.string()),
    }).superRefine((value, ctx) => {
        if (value.start_date && value.due_date && value.start_date > value.due_date) {
            ctx.addIssue({ code: 'custom', path: ['due_date'], message: t('task.due_before_start') })
        }

        if (value.milestone && !value.due_date) {
            ctx.addIssue({ code: 'custom', path: ['due_date'], message: t('task.milestone_needs_date') })
        }
    }))

    const request = (): ITaskUpdateRequest => taskUpdateRequest(props.task, {
        title:          form.value.title.trim(),
        description:    form.value.description,
        status:         form.value.status,
        priority:       form.value.priority,
        assignee_id:    form.value.assignee_id || null,
        start_date:     form.value.milestone ? form.value.due_date : form.value.start_date,
        due_date:       form.value.due_date,
        milestone:      form.value.milestone,
        estimate_hours: form.value.estimate_hours,
        progress:       Math.round(form.value.progress),
    })

    const sameSet = (a: string[], b: string[]): boolean =>
        a.length === b.length && a.every((id) => b.includes(id))

    const save = async () => {
        const body         = request()
        const fieldsChange = JSON.stringify(body) !== JSON.stringify(taskUpdateRequest(props.task))
        const depsChange   = !sameSet(form.value.depends_on, props.task.depends_on ?? [])

        if (!fieldsChange && !depsChange) {
            PrintSuccess(t('task.nothing_changed'))

            return
        }

        appStore.startLoading()

        const shifted: ITaskMin[] = []
        let saved: ITask | undefined

        try {
            if (fieldsChange) {
                const { statusCode, data } = await fetchStore.fetch(`/tasks/${props.task.id}`).put(body).json<IScheduleResult>()

                if (statusCode.value !== 200 || !data.value) {
                    const message = parseKnownError(data.value, t('task.save_failed'))
                    const code    = errorCode(data.value)

                    // Who may take it is said under the field it was chosen in.
                    if (code === Errors.AssigneeNotMember) {
                        formRef.value?.setErrors([{ name: 'assignee_id', message }])
                    }

                    PrintError(message)

                    return
                }

                saved = data.value.task
                shifted.push(...(data.value.shifted ?? []))
            }

            if (depsChange) {
                const { statusCode, data } = await fetchStore
                    .fetch(`/tasks/${props.task.id}/dependencies`)
                    .put({ depends_on: form.value.depends_on })
                    .json<IScheduleResult>()

                if (statusCode.value !== 200 || !data.value) {
                    const message = parseKnownError(data.value, t('task.save_failed'))
                    const code    = errorCode(data.value)

                    if (code === Errors.DependencyCycle || code === Errors.DependencyOtherProject) {
                        formRef.value?.setErrors([{ name: 'depends_on', message }])
                    }

                    PrintError(message)

                    // The fields may already be saved; the drawer reads the task again.
                    if (saved) emit('saved', saved, shifted)

                    return
                }

                saved = data.value.task
                shifted.push(...(data.value.shifted ?? []))
            }

            PrintSuccess(t('task.saved'))

            if (shifted.length) PrintNotice(t('schedule.shifted_title', shifted.length), formatShifted(shifted))

            if (saved) emit('saved', saved, shifted)
        } finally {
            appStore.stopLoading()
        }
    }

    const remove = async () => {
        const confirmed = await confirmDialog.value?.confirm(
            t('task.confirm_delete', { key: props.task.key, title: props.task.title }),
            { title: t('task.delete'), confirmLabel: t('general.delete') },
        )
        if (!confirmed) return

        appStore.startLoading()

        const { statusCode, data } = await fetchStore.fetch(`/tasks/${props.task.id}`).delete().json()

        appStore.stopLoading()

        if (statusCode.value === 204) {
            PrintSuccess(t('task.deleted'))
            emit('deleted', props.task.id)
        } else {
            PrintError(parseKnownError(data.value, t('task.delete_failed')))
        }
    }
</script>
